import React from 'react'

const barClass = (pct) => {
  if (pct === null || pct === undefined) return 'dbv2-fill-warn'
  if (pct >= 85) return 'dbv2-fill-good'
  if (pct >= 75) return 'dbv2-fill-warn'
  return 'dbv2-fill-bad'
}

const donutColor = (pct, target) => {
  if (pct === null || pct === undefined) return '#E4E7EC'
  if (pct >= target) return '#12B76A'
  if (pct >= target - 10) return '#F79009'
  return '#F04438'
}

// Noun for the "belum jatuh tempo" (not yet due) note, per metric.
const inWindowNoun = (key) => {
  if (key === 'penerbitan_sertifikat') return 'draft'
  if (key === 'submit_audit_report') return 'order'
  return 'item'
}

/**
 * Shared SLA panel (US-DB-5 Capaian SLA Audit / US-DB-6 SLA Veto Review):
 * donut with the overall achievement vs target, one bar per sub-metric,
 * and an optional real-time pending line (AC-14).
 */
export default function SlaPanel ({ title, subtitle, data, loading }) {
  if (loading && !data) {
    return (
      <div className='dbv2-card'>
        <p className='dbv2-card-title'>{title}</p>
        <div className='dbv2-loading'>Memuat data SLA…</div>
      </div>
    )
  }
  if (!data) return null

  const overall = data.overall
  const target = data.target
  const hasOverall = overall !== null && overall !== undefined
  const gap = hasOverall ? overall - target : null
  const donutPct = hasOverall ? Math.min(overall, 100) : 0
  const donutStyle = {
    background: `conic-gradient(${donutColor(overall, target)} 0% ${donutPct}%, #F2F4F7 ${donutPct}% 100%)`
  }

  const metrics = data.metrics || []
  const noData = metrics.every(
    (m) => m.hold || (m.achievedPct === null && !(m.inWindow > 0))
  )

  return (
    <div className='dbv2-card'>
      <p className='dbv2-card-title'>{title}</p>
      <p className='dbv2-card-subtitle'>{subtitle}</p>

      <div className='dbv2-sla-overall'>
        <div className='dbv2-donut' style={donutStyle}>
          <span>{hasOverall ? `${overall}%` : '—'}</span>
        </div>
        <div>
          <div className='dbv2-sla-target-label'>Target SLA</div>
          <div className='dbv2-sla-target-val'>{target}%</div>
          { hasOverall && gap < 0 &&
            <div className='dbv2-sla-warn'>▼ {Math.abs(gap)} poin di bawah target</div>
          }
          { hasOverall && gap >= 0 &&
            <div className='dbv2-sla-ok'>▲ mencapai target</div>
          }
          { !hasOverall &&
            <div className='dbv2-empty' style={{ padding: 0 }}>
              {noData ? 'Tidak ada data untuk periode yang dipilih.' : 'belum ada yang jatuh tempo pada periode ini'}
            </div>
          }
        </div>
      </div>

      {metrics.map((metric) => (
        <div className='dbv2-sla-bar-row' key={metric.key}>
          <div className='dbv2-sla-bar-label'>
            <span>
              {metric.label}
              { metric.targetDays > 0 &&
                <span className='dbv2-target'> (target {metric.targetDays} hari kerja)</span>
              }
              {metric.hold && <span className='dbv2-badge dbv2-badge-yellow' style={{ marginLeft: 6 }}>Hold</span>}
            </span>
            <span className='dbv2-pct'>
              { metric.hold
                ? <span className='dbv2-target'>belum digunakan</span>
                : <span>
                    {metric.achievedPct === null ? '—' : `${metric.achievedPct}%`}
                    { metric.totalInstances > 0 &&
                      <span className='dbv2-target'>
                        {` · ${metric.achievedInstances}/${metric.totalInstances}`}
                      </span>
                    }
                    { metric.inWindow > 0 &&
                      <span className='dbv2-target'>
                        {' '}·{' '}
                        <span
                          className='dbv2-tip'
                          data-tip={`${metric.inWindow} ${inWindowNoun(metric.key)} belum melewati batas waktu SLA, sehingga belum dihitung dalam capaian`}
                        >
                          {metric.inWindow} {inWindowNoun(metric.key)} belum jatuh tempo
                        </span>
                      </span>
                    }
                    { metric.unclassified > 0 &&
                      <span className='dbv2-target'>
                        {' '}·{' '}
                        <span
                          className='dbv2-tip'
                          data-tip={`${metric.unclassified} order tidak masuk perhitungan karena waktu submit-nya tidak dapat dicocokkan dengan jadwal audit — submit terjadi sebelum tanggal audit terakhir, atau data historis lama tanpa catatan waktu`}
                        >
                          {metric.unclassified} tidak masuk perhitungan
                        </span>
                      </span>
                    }
                  </span>
              }
            </span>
          </div>
          <div className='dbv2-sla-bar-track'>
            { !metric.hold && metric.achievedPct !== null &&
              <div
                className={`dbv2-sla-bar-fill ${barClass(metric.achievedPct)}`}
                style={{ width: `${Math.min(metric.achievedPct, 100)}%` }}
              />
            }
          </div>
        </div>
      ))}

      { data.pending && (() => {
        const windowDays = data.pending.windowDays || 365
        const windowLabel = windowDays % 365 === 0
          ? `${windowDays / 365} tahun`
          : `${windowDays} hari`
        return (
          <div className='dbv2-sla-pending'>
            Submission menunggu veto review saat ini: <b>{data.pending.count}</b>
            {data.pending.count > 0 && <span>, tertua <b>{data.pending.oldestDays}</b> hari</span>}
            {' '}·{' '}
            <span
              className='dbv2-tip'
              data-tip={`Jumlah order yang saat ini masih berstatus menunggu veto review, di antara yang masuk antrean dalam ${windowLabel} terakhir. Angka ini adalah potret antrean hari ini — tidak mengikuti filter periode di atas.`}
            >
              real-time, tidak mengikuti filter periode
            </span>
            {' '}·{' '}
            <span
              className='dbv2-tip'
              data-tip={`Cakupan data: hanya order yang masuk antrean veto dalam ${windowLabel} terakhir. Order yang menunggu lebih lama dari itu dianggap data lama yang menggantung dan tidak dihitung.`}
            >
              cakupan data {windowLabel} terakhir
            </span>
          </div>
        )
      })()}
    </div>
  )
}
